import { faMicrophone, faStop } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  isRecordingSupported,
  RECORDING_MAX_SECONDS,
  startWavRecording,
  type WavRecording,
} from "@phone/recordWav";
import { useSoftphoneStore } from "@phone/softphone";
import { Button, FieldError } from "@scribenote/brian-design-system";
import { useEffect, useRef, useState } from "react";

type Take =
  | { phase: "idle" }
  | { phase: "starting" }
  | { phase: "recording"; startedAt: number }
  | { phase: "encoding" }
  | { phase: "preview"; file: File; url: string }
  | { phase: "failed"; message: string };

/** Uses the headset's preferred microphone when one is saved. */
export const MessageRecorder = ({
  testIdPrefix,
  busy,
  onRecorded,
  maxSeconds = RECORDING_MAX_SECONDS,
}: {
  testIdPrefix: string;
  busy: boolean;
  onRecorded: (file: File) => Promise<boolean>;
  maxSeconds?: number;
}) => {
  const microphoneId = useSoftphoneStore((s) => s.microphoneId);
  const [take, setTake] = useState<Take>({ phase: "idle" });
  const [elapsed, setElapsed] = useState(0);
  const active = useRef<WavRecording | null>(null);
  const generation = useRef(0);
  const starting = useRef(false);
  const previewUrl = useRef<string | null>(null);
  const uploading = useRef(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (take.phase !== "recording") return;
    const tick = window.setInterval(() => {
      const seconds = Math.floor((Date.now() - take.startedAt) / 1000);
      setElapsed(seconds);
      if (seconds >= maxSeconds) void stop();
    }, 250);
    return () => window.clearInterval(tick);
  }, [take, maxSeconds]);

  useEffect(
    () => () => {
      generation.current += 1;
      active.current?.cancel();
      active.current = null;
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    },
    [],
  );

  if (!isRecordingSupported()) return null;

  const start = async () => {
    if (starting.current || active.current || busy) return;
    starting.current = true;
    const attempt = ++generation.current;
    setTake({ phase: "starting" });
    setElapsed(0);
    try {
      const recording = await startWavRecording(microphoneId, maxSeconds);
      if (attempt !== generation.current) {
        recording.cancel();
        return;
      }
      active.current = recording;
      setTake({ phase: "recording", startedAt: Date.now() });
    } catch {
      if (attempt !== generation.current) return;
      setTake({
        phase: "failed",
        message:
          "Couldn't start the microphone. Check the browser's permission.",
      });
    } finally {
      starting.current = false;
    }
  };

  const stop = async () => {
    const recording = active.current;
    if (!recording) return;
    active.current = null;
    const attempt = generation.current;
    setTake({ phase: "encoding" });
    try {
      const file = await recording.stop();
      if (attempt !== generation.current) return;
      const url = URL.createObjectURL(file);
      previewUrl.current = url;
      setTake({ phase: "preview", file, url });
    } catch {
      if (attempt !== generation.current) return;
      setTake({
        phase: "failed",
        message: "That recording couldn't be saved.",
      });
    }
  };

  const discard = () => {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
    setTake({ phase: "idle" });
  };

  const save = async () => {
    if (take.phase !== "preview" || busy || uploading.current) return;
    uploading.current = true;
    setSaving(true);
    const attempt = generation.current;
    try {
      if ((await onRecorded(take.file)) && attempt === generation.current)
        discard();
    } finally {
      uploading.current = false;
      if (attempt === generation.current) setSaving(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`${testIdPrefix}-recorder`}
    >
      {take.phase === "idle" || take.phase === "failed" ? (
        <div>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => void start()}
            data-testid={`${testIdPrefix}-record`}
          >
            <FontAwesomeIcon icon={faMicrophone} aria-hidden="true" />
            Record a message
          </Button>
        </div>
      ) : take.phase === "starting" ? (
        <span className="text-sm text-annotation">Opening the microphone…</span>
      ) : take.phase === "recording" ? (
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            onClick={() => void stop()}
            data-testid={`${testIdPrefix}-record-stop`}
          >
            <FontAwesomeIcon icon={faStop} aria-hidden="true" />
            Stop
          </Button>
          <span className="text-sm text-ink-body" aria-live="polite">
            Recording… {elapsed}s / {maxSeconds}s
          </span>
        </div>
      ) : take.phase === "encoding" ? (
        <span className="text-sm text-annotation">Saving the take…</span>
      ) : (
        <>
          <audio
            controls
            src={take.url}
            data-dd-privacy="hidden"
            className="w-full"
            data-testid={`${testIdPrefix}-record-preview`}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={busy || saving}
              onClick={() => void save()}
              data-testid={`${testIdPrefix}-record-use`}
            >
              Use this recording
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy || saving}
              onClick={discard}
            >
              Discard
            </Button>
          </div>
        </>
      )}
      {take.phase === "failed" && (
        <FieldError data-testid={`${testIdPrefix}-record-error`}>
          {take.message}
        </FieldError>
      )}
    </div>
  );
};
